import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import { HostedFields } from "braintree-web/modules/hosted-fields";
import { yupResolver } from "@hookform/resolvers/yup";
import { LayoutContext } from "Components/LayoutContext";
import { Button, SpinningButton } from "Components/ReactstrapSlim";
import { InputFL, InputFLProps, SelectInputFL } from "Components/Inputs";
import { CountryStateCodesRepsonse, CreditCardAuthorizedRequest, CreditCardData, ICreditCardData, ICreditCardAuthorizedRequest, PayableKey, PaymentMethodClient, PaymentProviderType, CreditCardPaymentMethodViewModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { BrainTreeFormModel, BrainTreeFormModelSchema, ExtendedHostedFieldsField, FieldsValidationState, FieldValidationState, getTypedKey } from "Utils/brainTreeHelper";
import { PaymentMethodDescriptor } from "Utils/paymentMethod";
import ReCAPTCHA from "react-google-recaptcha";
import classes from "./PaymentSettings.module.scss";

interface HostedFieldProps {
   id: string;
   label: string;
   state?: FieldValidationState;
   showErrors: boolean;
   size?: InputFLProps<object>["bsSize"];
   className?: string;
}

const HostedField: React.FC<HostedFieldProps> = function ({ id, label, state, showErrors, size, className }) {
   const isInvalid = state && !state.isValid && (showErrors || !state.isPotentiallyValid);
   const wrapperClasses = classNames(
      classes.pmfFormField,
      "rfl",
      {
         "rfl--invalid": isInvalid,
         "rfl--hasvalue": state && !state.isEmpty,
      },
      size ? `rfl-${size}` : null,
      className);

   return (
      <div className={wrapperClasses}>
         <div className={classNames("form-control bt-hosted-input", { [`form-control-${size}`]: !!size })} id={id}></div>
         {!isInvalid && <label htmlFor={id} className="rfl-label">
            {label}
         </label>}
         {isInvalid && <div role="alert" className="rfl-valdation-message">
            {state && state.isEmpty ? `${label} is required` : `${label} is not valid`}
         </div>}
      </div>
   );
}
export interface PaymentMethodFormBrainTreeProps {
   payableKey: PayableKey;
   editPaymentProfile?: CreditCardPaymentMethodViewModel;
   onSuccess?: (paymentMethod: PaymentMethodDescriptor) => void;
   onExit?: () => void;
   onDeleteClick?: (e: React.SyntheticEvent) => void;
}
const PaymentMethodFormBrainTree: React.FC<PaymentMethodFormBrainTreeProps> = function ({ payableKey, editPaymentProfile, onSuccess, onExit, onDeleteClick }) {
   const paymentMethodClient = useApiClient(PaymentMethodClient);
   const layoutContext = useContext(LayoutContext);
   const fieldSize = "lg";
   const [isApiLoaded, setIsApiLoaded] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [hostedFields, setHostedFields] = useState<HostedFields>();
   const [fieldsState, setFieldsState] = useState<FieldsValidationState>();
   const [_cardType, setCardType] = useState<string | undefined>();
   const [deviceData, setDeviceData] = useState<string>();
   const [braintreeFormValid, setBraintreeFormValid] = useState<boolean>(false);
   const [showErrors, setShowErrors] = useState<boolean>(false);
   const [stateCodes, setStateCodes] = useState<CountryStateCodesRepsonse>();
   const [recaptchaToken, setRecaptchaToken] = useState<string | undefined>(undefined);
   //let formDefaultValues: PermisionTabModel = useMemo(() => getPermisionTabModel(team), [team.id])
   const formMethods = useForm<BrainTreeFormModel>({
      resolver: yupResolver(BrainTreeFormModelSchema),
      mode: "onChange"
   });

   const watchCountry = formMethods.watch("country", "US");

   const save = () => {
      setShowErrors(true);
      setIsSaving(true);
      formMethods.trigger().then((isValid) => {
         if (braintreeFormValid && isValid && hostedFields) {
            const formValues = formMethods.getValues();
            hostedFields.tokenize(function (err, payload) {
               if (err) {
                  console.error(err);
                  setIsSaving(false);
                  return;
               }

               paymentMethodClient.createCreditCard(CreditCardAuthorizedRequest.fromJS({
                  payableKey: payableKey,
                  creditCardData: CreditCardData.fromJS({
                     ...formValues,
                     paymentMethodNonce: payload.nonce,
                     deviceData: deviceData,
                  } as ICreditCardData),
                  recaptchaToken: recaptchaToken
               } as ICreditCardAuthorizedRequest)).then(r => {
                  setIsSaving(false);
                  if (r.success && r.paymentProfile) {
                     if (onSuccess)
                        onSuccess({
                           paymentProvider: PaymentProviderType.BrainTree,
                           customerProfileId: r.paymentProfile.customerProfileId,
                           paymentProfileId: r.paymentProfile.paymentProfileId
                        });
                  } else {
                     layoutContext.showError(r);
                  }
               }).catch(err => {
                  setIsSaving(false);
                  layoutContext.showError(err);
               });
            });
         } else {
            setIsSaving(false);
            return;
         }
      });
   }

   useEffect(() => {
      if (editPaymentProfile) {
         Object.getOwnPropertyNames(editPaymentProfile).forEach(function (key) {
            const typedKey = getTypedKey(key);
            const value = editPaymentProfile[key];
            if (typedKey && value) {
               formMethods.setValue(typedKey, value)
            }
         });
      }
   }, [editPaymentProfile])

   useEffect(() => {
      paymentMethodClient.stateCodes()
         .then(r => {
            Object.keys(r.countryCodes).map(countryCode => {
               if (r.stateCodes[countryCode] === undefined) {
                  const jsStates = LiquidSpace.CountryStates.getStatesForCountry(countryCode);
                  if (jsStates) {
                     r.stateCodes[countryCode] = {};
                     jsStates.forEach(s => r.stateCodes[countryCode][s] = s);
                  }
               }
            });

            setStateCodes(r);
         });
   }, [])

   useEffect(() => {
      if (stateCodes !== undefined) {

         if (!editPaymentProfile || !editPaymentProfile.country) {
            formMethods.setValue("country", "US");
         }
         else {
            formMethods.setValue("country", editPaymentProfile.country);
         }
         if (editPaymentProfile?.state) {
            formMethods.setValue("state", editPaymentProfile.state);
         }
      }
   }, [editPaymentProfile, stateCodes])

   useEffect(() => {

      import("braintree-web").then(braintree => {
         braintree.client.create({
            authorization: LiquidSpaceConfig.BrainTreePublicToken || "",
         }, function (clientErr, clientInstance) {
            if (clientErr) {
               console.error("clientErr", clientErr);
               return;
            }
            // Create input fields and add text styles  
            braintree.hostedFields.create({
               client: clientInstance,
               //List of supported styles: https://braintree.github.io/braintree-web/3.58.0/module-braintree-web_hosted-fields.html#~styleOptions
               styles: {
                  "input": {
                     "color": "#00293a",
                     "font-size": "16px",
                     "font-weight": "700",
                     "font-family": "Arial,sans-serif",
                     "line-height": "24px",
                     "-webkit-appearance": "none"
                  },
                  // Style the text of an invalid input
                  "input.invalid": {
                     //'border-color': '#ff4d4d',
                  },
                  /*
                  // placeholder styles need to be individually adjusted
                  '::-webkit-input-placeholder': {
                     'color': 'rgba(0,0,0,0.6)'
                  },
                  ':-moz-placeholder': {
                     'color': 'rgba(0,0,0,0.6)'
                  },
                  '::-moz-placeholder': {
                     'color': 'rgba(0,0,0,0.6)'
                  },
                  ':-ms-input-placeholder': {
                     'color': 'rgba(0,0,0,0.6)'
                  }
                  */
               },
               // Add information for individual fields https://braintree.github.io/braintree-web/3.58.0/module-braintree-web_hosted-fields.html#~fieldOptions
               fields: {
                  number: {
                     selector: "#cc-number",
                     maxCardLength: 16,
                     supportedCardBrands: {
                        visa: true,
                        mastercard: true,
                        discover: true,
                        "american-express": true,
                        "diners-club": false,
                        jcb: false,
                        "union-pay": false,
                        maestro: false,
                        elo: false,
                        mir: false,
                        hiper: false,
                        hipercard: false
                     }
                  } as ExtendedHostedFieldsField,
                  cvv: {
                     selector: "#cc-cvv",
                  },
                  /*
                  expirationMonth: {
                     selector: '#cc-exp-month',
                     select: true,
                     placeholder: '--',
                  },
                  expirationYear: {
                     selector: '#cc-exp-year',
                     select: true,
                     placeholder: '----',
                  },
                  */
                  expirationDate: {
                     selector: "#cc-exp",
                     //placeholder: 'MM/YYYY',
                     prefill: editPaymentProfile?.expirationDateRaw || undefined
                  },
                  postalCode: {
                     selector: "#cc-zip",
                     prefill: editPaymentProfile?.zip
                  },
               }
            }, function (err, hostedFieldsInstance: HostedFields) {
               if (err) {
                  console.error("hostedFieldsInstance err", err);
                  return;
               }
               setHostedFields(hostedFieldsInstance);
               setFieldsState(hostedFieldsInstance.getState().fields);
               setIsApiLoaded(true);


               hostedFieldsInstance.on("validityChange", function (event) {
                  setFieldsState(event.fields);
                  let localBraintreeFormValid = true;
                  Object.getOwnPropertyNames(event.fields).forEach(function (key) {
                     const state = event.fields[key];
                     localBraintreeFormValid = localBraintreeFormValid && state.isValid;
                  });
                  setBraintreeFormValid(localBraintreeFormValid);
               });

               hostedFieldsInstance.on("empty", function (event) {
                  setFieldsState((state) => {
                     if (state && event.emittedBy in state) {
                        const newState = {
                           ...state
                        } as FieldsValidationState;
                        newState[event.emittedBy].isEmpty = true;
                        return newState;
                     }
                     return state;
                  });
               });
               hostedFieldsInstance.on("notEmpty", function (event) {
                  setFieldsState((state) => {
                     if (state && event.emittedBy in state) {
                        const newState = {
                           ...state
                        } as FieldsValidationState;
                        newState[event.emittedBy].isEmpty = false;
                        return newState;
                     }
                     return state;
                  });
               });

               hostedFieldsInstance.on("cardTypeChange", function (event) {
                  let type: string | undefined = undefined;
                  if (event.cards.length === 1) {
                     type = event.cards[0].type;
                  }
                  setCardType(type);
               });


            });


            //Setup data collection for Kount
            braintree.dataCollector.create({
               client: clientInstance,
               kount: true,
               paypal: false
            }, function (err, dataCollectorInstance) {
               if (err) {
                  console.log("braintree.dataCollector initialization failed.", err)
                  // Handle error in creation of data collector
                  return;
               }
               setDeviceData(dataCollectorInstance.deviceData);
            });
         });
      });
   }, []);

   useEffect(() => {
      const state = formMethods.getValues().state;
      const codes = stateCodes?.stateCodes?.[watchCountry ?? ""];

      if (watchCountry && codes) {
         const isValidState = Object.values(codes)?.some((s) => s === state);
         formMethods.setValue(
            "state",
            isValidState ? state : Object.values(codes)?.[0]
         );
         formMethods.trigger("state");
      } else {
         formMethods.setValue("state", "");
      }
   }, [watchCountry, stateCodes]);

   return (
      <div className={classes.paymentMethodFormB}>
         <div className={classes.pmfForm}>
            <InputFL bsSize={fieldSize} control={formMethods.control} name="fullName" className={classes.pmfFormField} placeholder="Cardholder name" />
            <InputFL bsSize={fieldSize} control={formMethods.control} name="address" className={classes.pmfFormField} placeholder="Address" />
            <InputFL bsSize={fieldSize} control={formMethods.control} name="city" className={classes.pmfFormField} placeholder="City" />
            <SelectInputFL bsSize={fieldSize} control={formMethods.control} name="country" className={classes.pmfFormField} placeholder="Country">
               {
                  stateCodes && stateCodes.countryCodes
                     ? Object.keys(stateCodes.countryCodes).map(c => <option key={c} value={c}>{stateCodes && stateCodes.countryCodes && stateCodes.countryCodes[c]}</option>)
                     : null
               }
            </SelectInputFL>
            {stateCodes?.stateCodes && watchCountry && watchCountry in stateCodes.stateCodes
               ? <SelectInputFL bsSize={fieldSize} control={formMethods.control} name="state" className={classes.pmfFormField} placeholder="State">
                  <option disabled hidden></option>
                  {
                     Object.keys(stateCodes.stateCodes[watchCountry]).map(c => <option key={c} value={stateCodes?.stateCodes && stateCodes.stateCodes[watchCountry][c]}>{c}</option>)
                  }
               </SelectInputFL>
               : <InputFL bsSize={fieldSize} control={formMethods.control} name="state" className={classes.pmfFormField} placeholder="State" />
            }
            <HostedField size={fieldSize} id="cc-zip" label="ZIP/postal code" state={fieldsState && fieldsState["postalCode"]} showErrors={showErrors} />
            <HostedField size={fieldSize} id="cc-number" label="Card number" state={fieldsState && fieldsState["number"]} showErrors={showErrors} />
            <HostedField size={fieldSize} id="cc-exp" label="Exp. date" className={classes.pmfFormFieldSmall} state={fieldsState && fieldsState["expirationDate"]} showErrors={showErrors} />
            <HostedField size={fieldSize} id="cc-cvv" label="CVV" className={classes.pmfFormFieldSmall} state={fieldsState && fieldsState["cvv"]} showErrors={showErrors} />
            <div>
               <ReCAPTCHA
                  sitekey={LiquidSpaceConfig.RecaptchaKeys.Explicit.SiteKey || ""}
                  onChange={(token) => setRecaptchaToken(token || undefined)}
               />
            </div>
         </div>
         <div className={classNames(classes.pmfButtons , { "d-none": !isApiLoaded })} >
            <Button color="secondary" onClick={() => { onExit && onExit(); }}>Cancel</Button>
            {editPaymentProfile && <Button color="secondary" onClick={onDeleteClick}>Delete</Button>}
            <SpinningButton color="primary" onClick={(e) => { e.preventDefault(); save(); }} isLoading={isSaving}>
               Save
            </SpinningButton>
         </div>
      </div>
   );
}

export default PaymentMethodFormBrainTree;